import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { getComicsByCategory, latestComics, popularComics, topRatedComics } from '@/mocks/comics';
import { categories } from '@/mocks/categories';
import type { Comic } from '@/mocks/comics';

type SortType = 'latest' | 'popular' | 'rating';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [comics, setComics] = useState<Comic[]>([]);
  const [sortType, setSortType] = useState<SortType>('latest');
  const currentSlug = slug || 'all';

  const currentCategory = categories.find((c) => c.slug === currentSlug);

  useEffect(() => {
    let result = getComicsByCategory(currentSlug);
    switch (sortType) {
      case 'popular':
        result = [...result].sort((a, b) => b.views - a.views);
        break;
      case 'rating':
        result = [...result].sort((a, b) => b.rating - a.rating);
        break;
      default:
        result = [...result].sort(
          (a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()
        );
    }
    setComics(result);
  }, [currentSlug, sortType]);

  return (
    <div className="min-h-screen pt-16">
      <div className="bg-background-100/70 py-8">
        <div className="w-full max-w-[1280px] mx-auto">
          <Breadcrumb
            items={[
              { label: t('breadcrumb.home'), href: '/' },
              { label: currentCategory?.name || t('category.allComics') },
            ]}
          />
          <div className="px-4 md:px-6 mt-4">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900">
              {currentCategory?.name || t('category.title')}
            </h1>
            <p className="text-sm text-foreground-500 mt-1">
              {t('category.resultCount', { count: comics.length })}
            </p>
          </div>
        </div>
      </div>

      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-8">
        {/* Category Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-6 flex-wrap">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                currentSlug === cat.slug
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <i className={`${cat.icon} text-sm`}></i>
              {cat.name}
            </Link>
          ))}
        </div>

        {/* Sort Tabs */}
        <div className="flex items-center gap-1 mb-6 bg-background-100 rounded-full p-1 w-fit">
          {([
            { key: 'latest', label: t('category.sortNewest') },
            { key: 'popular', label: t('category.sortPopular') },
            { key: 'rating', label: t('category.sortRating') },
          ] as { key: SortType; label: string }[]).map((s) => (
            <button
              key={s.key}
              onClick={() => setSortType(s.key)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                sortType === s.key
                  ? 'bg-background-50 text-foreground-900'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Comics Grid */}
        {comics.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
            {comics.map((comic) => (
              <ComicCard key={`${currentSlug}-${comic.id}`} comic={comic} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500">{t('category.noComics')}</p>
          </div>
        )}
      </div>

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategory?.name || '全部'} - 韩漫之家`,
            description: `韩漫之家${currentCategory?.name || ''}分类，收录优质韩国漫画在线阅读资源`,
            url: `https://yangyijixie.cn/category/${currentSlug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '韩漫之家',
              url: 'https://yangyijixie.cn',
            },
          }),
        }}
      />
    </div>
  );
}